(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sectionSelector = '#MainContent > .shopify-section, .shopify-section-group-footer-group > .shopify-section';
  const skipSectionSelector = ':scope .a-hero, :scope .a-mq';
  const targetGroups = [
    {
      effect: 'eyebrow',
      selector: '.a-eyebrow, .a-label, .asama-split__label, .asama-contact__label, .a-ft__k',
    },
    {
      effect: 'heading',
      selector: 'h1, h2, h3, blockquote.a-display, .a-display',
    },
    {
      effect: 'copy',
      selector: 'p:not(.a-eyebrow):not(.a-label), figcaption, dt, dd, .rte, .a-lede, .asama-page__lede, .a-impact__body, .a-gift__text, .a-craft__body, .asama-split__text, .asama-page__note, .a-ft__desc, .a-ft__lede, .card__heading, .price, .caption',
    },
    {
      effect: 'media',
      selector: '.a-cat__tile, .a-craft__frame, .a-gift__frame, .asama-page__media, .asama-split__media, .product__media-item, .article-card__image-wrapper, .card__media',
    },
    {
      effect: 'left',
      selector: '.a-impact__head, .a-gift__box, .asama-contact__head, .asama-enquiry__head, .asama-steps__head, .asama-measure__head, .asama-faq__head',
    },
    {
      effect: 'right',
      selector: '.asama-contact__form-panel, .asama-enquiry__form, .a-quote__slider, .asama-table__scroll',
    },
    {
      effect: 'card',
      selector: '.a-impact__stat, .asama-steps__item, .asama-faq__item, .asama-contact__channel, .asama-contact__rail-item, .a-gift__patron, .a-craft__note, .grid__item, .a-ft__cols > *, .asama-measure__key',
    },
    {
      effect: 'line',
      selector: '.a-sec__more, .a-impact__cta, .asama-split__pills, .asama-trust__item, .a-ft__base',
    },
    {
      effect: 'action',
      selector: '.button, .a-more, .a-link, .slider-button',
    },
  ];

  function formatCounter(value, decimals) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  function prepareCounters(root = document) {
    root.querySelectorAll('[data-asama-counter]:not([data-counter-ready])').forEach((counter) => {
      const raw = counter.dataset.asamaCounter.trim();
      const target = Number.parseFloat(raw.replace(/,/g, ''));
      if (!Number.isFinite(target)) return;
      counter.dataset.counterReady = 'true';
      const decimalPart = raw.replace(/,/g, '').split('.')[1];
      const decimals = decimalPart ? decimalPart.length : 0;
      const finish = () => { counter.textContent = formatCounter(target, decimals); };

      if (reducedMotion || !('IntersectionObserver' in window)) {
        finish();
        return;
      }

      counter.textContent = formatCounter(0, decimals);
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        const started = performance.now();
        function tick(now) {
          const progress = Math.min((now - started) / 600, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          counter.textContent = formatCounter(target * eased, decimals);
          if (progress < 1) requestAnimationFrame(tick);
          else finish();
        }
        requestAnimationFrame(tick);
      }, { threshold: 0.35 });
      observer.observe(counter);
    });
  }

  function wrapHeadingWords(heading) {
    if (heading.dataset.asamaWords || heading.closest('.a-hero')) return;
    heading.dataset.asamaWords = 'true';
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) {
      if (walker.currentNode.nodeValue.trim()) textNodes.push(walker.currentNode);
    }

    let wordIndex = 0;
    textNodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part.trim()) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const clip = document.createElement('span');
        const word = document.createElement('span');
        clip.className = 'a-motion-word-clip';
        word.className = 'a-motion-word';
        word.style.setProperty('--a-word-delay', `${Math.min(wordIndex * 24, 120)}ms`);
        word.textContent = part;
        clip.appendChild(word);
        fragment.appendChild(clip);
        wordIndex += 1;
      });
      node.replaceWith(fragment);
    });
  }

  function addTarget(target, effect, order) {
    if (target.dataset.asamaMotion) return false;
    target.dataset.asamaMotion = effect;
    target.style.setProperty('--a-motion-delay', `${Math.min(order * 40, 120)}ms`);
    target.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
    if (effect === 'heading') wrapHeadingWords(target);
    return true;
  }

  function prepareSections(root = document) {
    const sections = root.matches?.(sectionSelector) ? [root] : [...root.querySelectorAll(sectionSelector)];
    sections.forEach((section) => {
      if (section.dataset.asamaMotionReady || section.querySelector(skipSectionSelector)) return;
      section.dataset.asamaMotionReady = 'true';
      const targets = [];

      targetGroups.forEach((group) => {
        [...section.querySelectorAll(group.selector)].forEach((target, index) => {
          if (target.closest('[data-asama-motion]') && group.effect !== 'heading') return;
          if (addTarget(target, group.effect, index)) targets.push(target);
        });
      });

      if (!targets.length && addTarget(section, 'surface', 0)) targets.push(section);
      if (reducedMotion || !('IntersectionObserver' in window)) {
        targets.forEach((target) => target.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
      targets.forEach((target) => observer.observe(target));
    });
  }

  function init(root = document) {
    prepareCounters(root);
    prepareSections(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
